/**
 * PR4 — bandeja IN-APP del tenant (campana). Avisos de cancelación/reagenda de
 * reserva; los de cancelación CON CFDI timbrado son ACCIONABLES (persisten con
 * badge ámbar hasta marcarse vistos — es una acción fiscal que no se pierde).
 * NO hay correo al tenant; esto es el canal. Se hidrata de /me/notifications.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, AlertTriangle, Check, X } from 'lucide-react';
import { api } from '../lib/api';

interface Notif {
  id: string;
  kind: string;
  severity: 'info' | 'action';
  title: string;
  body: string;
  actionRequired: boolean;
  cfdiFolio: string | null;
  cfdiTotal: number | null;
  seenAt: string | null;
  createdAt: string;
}
interface Feed { items: Notif[]; unseen: number; actionable: number; }
interface Settings { notifyUserId: string | null; users: Array<{ id: string; name: string; email: string }>; }

const when = (iso: string) => {
  try { return new Date(iso).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

export function NotificationsBell({ enabled = true }: { enabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState<Feed>({ items: [], unseen: 0, actionable: 0 });
  const [settings, setSettings] = useState<Settings | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    if (!enabled) return;
    api('/me/notifications').then((r) => setFeed(r as Feed)).catch(() => { /* sin permiso / offline */ });
  }, [enabled]);

  // Carga inicial + poll ligero (60s) para que el badge refleje cambios sin recargar.
  useEffect(() => {
    if (!enabled) return;
    load();
    const t = window.setInterval(load, 60_000);
    return () => window.clearInterval(t);
  }, [enabled, load]);

  // Cerrar al hacer click afuera.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const openPanel = () => {
    setOpen((v) => !v);
    if (!open) {
      load();
      if (!settings) api('/me/notifications/settings').then((r) => setSettings(r as Settings)).catch(() => {});
    }
  };

  const markSeen = async (id: string) => {
    setFeed((f) => {
      const items = f.items.map((i) => (i.id === id ? { ...i, seenAt: new Date().toISOString() } : i));
      return { items, unseen: items.filter((i) => !i.seenAt).length, actionable: items.filter((i) => !i.seenAt && i.actionRequired).length };
    });
    try { await api(`/me/notifications/${id}/seen`, { method: 'POST' }); } catch { load(); }
  };

  const setContact = async (userId: string) => {
    const v = userId || null;
    setSettings((s) => (s ? { ...s, notifyUserId: v } : s));
    try { await api('/me/notifications/settings', { method: 'PUT', body: { notifyUserId: v } }); } catch { /* revertirá al recargar */ }
  };

  if (!enabled) return null;
  const badge = feed.actionable > 0 ? feed.actionable : feed.unseen;
  const badgeColor = feed.actionable > 0 ? 'bg-amber-500' : 'bg-rose-600';

  return (
    <div className="relative" ref={ref}>
      <button onClick={openPanel} title="Avisos"
        className="relative shrink-0 rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50">
        <Bell className="h-4 w-4" />
        {badge > 0 && (
          <span className={`absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white ${badgeColor}`}>
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-[22rem] max-w-[90vw] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <p className="text-sm font-semibold text-slate-800">Avisos</p>
            <button onClick={() => setOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>

          <div className="max-h-[24rem] overflow-y-auto">
            {feed.items.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">Sin avisos.</p>
            ) : feed.items.map((n) => {
              const pending = n.actionRequired && !n.seenAt;
              return (
                <div key={n.id} className={`border-b border-slate-50 px-4 py-3 ${pending ? 'bg-amber-50/60' : n.seenAt ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-2">
                    {pending && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-slate-800">{n.title}</p>
                      <p className="mt-0.5 text-[12px] leading-snug text-slate-600">{n.body}</p>
                      <div className="mt-1.5 flex items-center justify-between">
                        <span className="text-[11px] text-slate-400">{when(n.createdAt)}</span>
                        {!n.seenAt && (
                          <button onClick={() => markSeen(n.id)}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-900">
                            <Check className="h-3 w-3" /> {pending ? 'Marcar resuelto' : 'Marcar visto'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {settings && settings.users.length > 0 && (
            <div className="border-t border-slate-100 bg-slate-50 px-4 py-2.5">
              <label className="flex items-center justify-between gap-2 text-[12px] text-slate-600">
                <span>Contacto de avisos</span>
                <select value={settings.notifyUserId ?? ''} onChange={(e) => setContact(e.target.value)}
                  className="max-w-[11rem] rounded-lg border border-slate-300 bg-white px-2 py-1 text-[12px]">
                  <option value="">Todo el equipo</option>
                  {settings.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
